import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { ChevronLeft, BarChart3, Image as ImgIcon, Video, FileText } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const STATUS_BADGE = {
  pending:    "bg-[#F4F4F0] text-[#5C5C5C] border border-[#0A0A0A]",
  generating: "bg-[#FFD600] text-[#0A0A0A]",
  scheduled:  "bg-[#0A0A0A] text-[#FFD600]",
  failed:     "bg-[#E53935] text-white",
  cancelled:  "bg-[#5C5C5C] text-white",
};
const KIND_ICON = { post: FileText, image: ImgIcon, video: Video };

export default function CampaignDetail() {
  const { id } = useParams();
  const [campaign, setCampaign] = useState(null);
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("items"); // items | report

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, it, r] = await Promise.all([
        api.get(`/campaigns/${id}`),
        api.get(`/campaigns/${id}/items`),
        api.get(`/campaigns/${id}/report`).catch(() => ({ data: null })),
      ]);
      setCampaign(c.data);
      setItems(it.data || []);
      setReport(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando campaña");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (loading && !campaign) return <div className="font-mono-data text-sm">Cargando…</div>;
  if (!campaign) return <div className="font-mono-data text-sm text-[#E53935]">Campaña no encontrada</div>;

  return (
    <div className="space-y-6" data-testid="campaign-detail">
      <Link to="/dashboard/campaigns" className="font-mono-mark text-[10px] text-[#5C5C5C] hover:text-[#0A0A0A] flex items-center gap-1 w-fit">
        <ChevronLeft className="w-3 h-3" /> CAMPAÑAS
      </Link>

      <div>
        <div className="font-mono-mark text-[10px] text-[#5C5C5C]">
          {new Date(campaign.start_date).toLocaleDateString("es-MX")} → {new Date(campaign.end_date).toLocaleDateString("es-MX")} · {campaign.duration_days} DÍAS · {campaign.objective?.toUpperCase()} · {campaign.tone?.toUpperCase()}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mt-2 leading-none">{campaign.name}</h1>
        <div className="flex flex-wrap gap-2 mt-3">
          {(campaign.networks || []).map((n) => (
            <span key={n} className="font-mono-mark text-[9px] border border-[#0A0A0A] px-2 py-1">{n.toUpperCase()}</span>
          ))}
          <span className={`font-mono-mark text-[9px] px-2 py-1 ${campaign.status === "active" ? "bg-[#FFD600]" : campaign.status === "completed" ? "bg-[#0A0A0A] text-[#FFD600]" : "bg-[#5C5C5C] text-white"}`}>
            {campaign.status?.toUpperCase()}
          </span>
        </div>
      </div>

      <div className="flex gap-1 border-b border-[#0A0A0A]">
        <button onClick={() => setTab("items")} data-testid="tab-items" className={`font-mono-mark text-[10px] px-4 py-3 ${tab === "items" ? "bg-[#0A0A0A] text-white" : "text-[#5C5C5C] hover:text-[#0A0A0A]"}`}>
          PIEZAS ({items.length})
        </button>
        <button onClick={() => setTab("report")} data-testid="tab-report" className={`font-mono-mark text-[10px] px-4 py-3 ${tab === "report" ? "bg-[#0A0A0A] text-white" : "text-[#5C5C5C] hover:text-[#0A0A0A]"}`}>
          <BarChart3 className="w-3 h-3 inline-block mr-1" /> REPORTE
        </button>
      </div>

      {tab === "items" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.length === 0 && <div className="font-mono-data text-sm text-[#5C5C5C]">No hay piezas planificadas todavía.</div>}
          {items.map((it) => <ItemCard key={it.id} item={it} campaignId={id} />)}
        </div>
      )}

      {tab === "report" && (
        <Report campaign={campaign} report={report?.report} isPartial={report?.is_partial} />
      )}
    </div>
  );
}

function ItemCard({ item, campaignId }) {
  const Icon = KIND_ICON[item.kind] || FileText;
  const [imgB64, setImgB64] = useState(null);

  useEffect(() => {
    if (item.has_image && !imgB64) {
      api.get(`/campaigns/${campaignId}/items/${item.id}/image`)
        .then(({ data }) => setImgB64(data.image_b64))
        .catch(() => {});
    }
  }, [item.has_image, item.id, campaignId, imgB64]);

  return (
    <div className="bg-white border border-[#0A0A0A] p-3 flex flex-col gap-2" data-testid={`item-${item.id}`}>
      <div className="flex justify-between items-center">
        <span className={`font-mono-mark text-[9px] px-2 py-1 ${STATUS_BADGE[item.status] || ""}`}>
          {item.status?.toUpperCase()}
        </span>
        <span className="font-mono-mark text-[9px] text-[#5C5C5C] flex items-center gap-1">
          <Icon className="w-3 h-3" /> {item.network?.toUpperCase()}
        </span>
      </div>
      {imgB64 && (
        <img src={`data:image/png;base64,${imgB64}`} alt="" className="w-full aspect-square object-cover" />
      )}
      <div className="font-mono-data text-[11px] leading-relaxed line-clamp-5">
        {item.generated_text || <span className="text-[#5C5C5C] italic">Pendiente de generar…</span>}
      </div>
      <div className="font-mono-mark text-[9px] text-[#5C5C5C] mt-auto pt-2 border-t border-[#F4F4F0]">
        {new Date(item.scheduled_at).toLocaleString("es-MX")}
      </div>
      {item.error && <div className="font-mono-data text-[10px] text-[#E53935]">{item.error}</div>}
    </div>
  );
}

function Report({ campaign, report, isPartial }) {
  if (!report) return <div className="font-mono-data text-sm text-[#5C5C5C]">Generando reporte…</div>;

  const t = report.totals || {};
  return (
    <div className="space-y-4" data-testid="campaign-report">
      {isPartial && (
        <div className="font-mono-data text-xs bg-[#FFD600] p-3 border-l-4 border-[#0A0A0A]">
          ℹ️ Reporte EN VIVO. La campaña aún está activa. Las métricas finales se calcularán al cierre el {new Date(campaign.end_date).toLocaleDateString("es-MX")}.
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="PUBLICADAS" value={t.published || 0} />
        <KPI label="PROGRAMADAS" value={t.scheduled || 0} />
        <KPI label="FALLIDAS" value={t.failed || 0} />
        <KPI label="ENGAGEMENT" value={`${report.engagement_rate || 0}%`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="VIEWS" value={(t.views || 0).toLocaleString("es-MX")} />
        <KPI label="LIKES" value={(t.likes || 0).toLocaleString("es-MX")} />
        <KPI label="COMMENTS" value={(t.comments || 0).toLocaleString("es-MX")} />
        <KPI label="SHARES" value={(t.shares || 0).toLocaleString("es-MX")} />
      </div>
      <div className="bg-white border border-[#0A0A0A]">
        <div className="font-mono-mark text-[10px] p-3 border-b border-[#0A0A0A] bg-[#0A0A0A] text-white">DESGLOSE POR RED</div>
        <table className="w-full font-mono-data text-xs">
          <thead className="bg-[#F4F4F0]">
            <tr>
              <th className="text-left p-2">RED</th>
              <th className="text-right p-2">TOTAL</th>
              <th className="text-right p-2">PUBL.</th>
              <th className="text-right p-2">FALL.</th>
              <th className="text-right p-2">VIEWS</th>
              <th className="text-right p-2">LIKES</th>
            </tr>
          </thead>
          <tbody>
            {(report.by_network || []).map((row) => (
              <tr key={row.network} className="border-t border-[#F4F4F0]">
                <td className="p-2 font-mono-mark text-[10px]">{row.network?.toUpperCase()}</td>
                <td className="p-2 text-right">{row.total}</td>
                <td className="p-2 text-right">{row.published}</td>
                <td className="p-2 text-right text-[#E53935]">{row.failed}</td>
                <td className="p-2 text-right">{(row.views || 0).toLocaleString("es-MX")}</td>
                <td className="p-2 text-right">{(row.likes || 0).toLocaleString("es-MX")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {report.note && (
        <div className="font-mono-data text-xs text-[#5C5C5C] bg-[#F4F4F0] p-3 border-l-4 border-[#5C5C5C]">{report.note}</div>
      )}
    </div>
  );
}

function KPI({ label, value }) {
  return (
    <div className="border border-[#0A0A0A] bg-white p-4">
      <div className="font-mono-mark text-[9px] text-[#5C5C5C]">{label}</div>
      <div className="font-display text-3xl mt-1">{value}</div>
    </div>
  );
}
